/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import i18n from 'i18next'
import Cookies from 'js-cookie'
import moment from 'moment'
import 'moment/locale/pt-br'
import { Suspense, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { I18nextProvider } from 'react-i18next'
import { RouterProvider } from 'react-router-dom'
import { Toaster } from 'sonner'
import { ThemeProvider } from 'styled-components'
import { Loader } from './components'
import locales from './locales'
import routes from './routes'
import theme from './theme'

const authLink = setContext((_, { headers }) => {
  const token = Cookies.get('access_token')
  // get the authentication token from local storage if it exists
  // const token = localStorage.getItem('token');
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    }
  }
})

const client = new ApolloClient({
  cache: new InMemoryCache({ addTypename: false }),
  link: authLink.concat(
    createUploadLink({
      uri: process.env.REACT_APP_WEBSITE_API_URL ?? '/api',
      headers: {
        'Apollo-Require-Preflight': 'true'
      }
    })
  )
})

export default function App () {
  useEffect(() => {
    // Set the moment locale to pt-br
    moment.locale('pt-br')
    // Set the i18n locale to pt-BR
    i18n.changeLanguage('pt-BR')
  }, [])

  return (
    <I18nextProvider i18n={locales}>
      <>
        <Toaster position='bottom-center' />
        <Helmet
          defaultTitle={process.env.REACT_APP_WEBSITE_NAME ?? 'Supermercados Mundial Back-office'}
          titleTemplate={`%s - ${process.env.REACT_APP_WEBSITE_NAME ?? 'Supermercados Mundial Back-office'}`}
        />
        <Suspense fallback={<Loader />}>
          <ApolloProvider client={client}>
            <ThemeProvider theme={theme}>
              <RouterProvider
                router={routes}
                fallbackElement={<Loader />}
              />
            </ThemeProvider>
          </ApolloProvider>
        </Suspense>
      </>
    </I18nextProvider>
  )
}
